/**
 * Eingaben der Einstellungen im Board-Stil: kleine Mono-Beschriftung über einem großen Wert, Einheit
 * rechts, Fehler rot am Rahmen und als Satz darunter (per `aria-describedby` vorgelesen).
 */
import {
  forwardRef,
  useId,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
} from 'react';
import { cn } from '@/shared/lib/cn';

const frame = (error?: string) =>
  cn(
    'rounded-control focus-within:outline-forest flex min-h-[60px] min-w-0 flex-col justify-center gap-0.5 px-3.5 py-2 focus-within:outline focus-within:outline-2',
    error ? 'border-sealwax bg-sealwax-tint/50 border-[1.5px]' : 'border-line bg-card border',
  );
const caption = 'text-ink-soft font-mono text-[10px] uppercase tracking-[0.14em]';

interface FieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  error?: string;
  suffix?: string;
}

export const ConfigurationField = forwardRef<HTMLInputElement, FieldProps>(
  ({ label, error, suffix, className, ...props }, ref) => {
    const id = useId();
    return (
      <div className={cn('min-w-0', className)}>
        <label htmlFor={id} className={frame(error)}>
          <span className={caption}>{label}</span>
          <span className="flex items-baseline gap-1">
            <input
              ref={ref}
              id={id}
              aria-label={label}
              aria-invalid={Boolean(error)}
              aria-describedby={error ? `${id}-error` : undefined}
              className="text-ink w-full min-w-0 bg-transparent text-lg font-bold outline-none disabled:opacity-50"
              {...props}
            />
            {suffix && <span className="text-ink-soft text-sm">{suffix}</span>}
          </span>
        </label>
        {error && (
          <p id={`${id}-error`} className="text-sealwax mt-1 text-xs">
            {error}
          </p>
        )}
      </div>
    );
  },
);
ConfigurationField.displayName = 'ConfigurationField';

interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label: string;
  children: ReactNode;
  error?: string;
}

export const ConfigurationSelect = forwardRef<HTMLSelectElement, SelectProps>(
  ({ label, children, error, ...props }, ref) => {
    const id = useId();
    return (
      <div className="min-w-0">
        <label htmlFor={id} className={frame(error)}>
          <span className={caption}>{label}</span>
          <select
            ref={ref}
            id={id}
            aria-label={label}
            aria-invalid={Boolean(error)}
            className="text-ink -ml-1 w-full min-w-0 bg-transparent text-base font-bold outline-none"
            {...props}
          >
            {children}
          </select>
        </label>
        {error && <p className="text-sealwax mt-1 text-xs">{error}</p>}
      </div>
    );
  },
);
ConfigurationSelect.displayName = 'ConfigurationSelect';

interface SwitchProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  hint?: string;
}

/** Schalter als Kästchen mit `role="switch"` – react-hook-form registriert ihn wie ein Kästchen. */
export const ConfigurationSwitch = forwardRef<HTMLInputElement, SwitchProps>(
  ({ label, hint, className, ...props }, ref) => {
    const id = useId();
    return (
      <label
        className={cn(
          'border-hairline bg-card has-[:focus-visible]:outline-forest flex min-h-[52px] cursor-pointer items-center gap-3 rounded-lg border px-3.5 py-2 has-[:disabled]:cursor-not-allowed has-[:disabled]:opacity-55 has-[:focus-visible]:outline has-[:focus-visible]:outline-2',
          className,
        )}
      >
        <input
          ref={ref}
          type="checkbox"
          role="switch"
          aria-label={label}
          aria-describedby={hint ? `${id}-hint` : undefined}
          className="peer sr-only"
          {...props}
        />
        <span
          aria-hidden
          className="bg-line peer-checked:bg-forest relative h-6 w-10 flex-none rounded-full transition-colors after:absolute after:left-[3px] after:top-[3px] after:size-[18px] after:rounded-full after:bg-white after:transition-transform peer-checked:after:translate-x-4"
        />
        <span className="flex min-w-0 flex-col">
          <span className="text-sm font-semibold">{label}</span>
          {hint && (
            <span id={`${id}-hint`} className="text-ink-soft text-xs">
              {hint}
            </span>
          )}
        </span>
      </label>
    );
  },
);
ConfigurationSwitch.displayName = 'ConfigurationSwitch';
